import { isTouchDevice } from '../input/touch';
import { browserLabel, type CrashBody, type CrashKind, type FeedbackBody } from './beta';

/**
 * The game's side of the beta's channels back (src/net/beta.ts): crash reports, sent the moment something
 * goes wrong, and the feedback a player types. Screenshot runs and robots send nothing.
 */

declare const __BUILD__: string;
/** Which build of the game this is (stamped by vite.config.ts). */
const BUILD = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';

const quiet = (): boolean =>
  /bot|crawl|spider|headless|lighthouse|preview/i.test(navigator.userAgent) ||
  // A browser driven by a program (site scanners, test rigs), whatever it calls itself.
  navigator.webdriver === true ||
  new URLSearchParams(location.search).has('shot');

const device = (): 'phone' | 'computer' => (isTouchDevice() ? 'phone' : 'computer');

const browser = (): string => browserLabel(navigator.userAgent).slice(0, 60);

/** At most a few reports a visit, each problem once. */
const MAX_REPORTS = 5;
let reports = 0;
const reported = new Set<string>();

/** Send what went wrong: a crash, an error the game carried on through, no 3D at all, or the 3D lost. */
export function reportProblem(kind: CrashKind, err: unknown, mode = ''): void {
  if (quiet() || reports >= MAX_REPORTS) return;
  const e = err instanceof Error ? err : null;
  const message = (e ? `${e.name}: ${e.message}` : String(err ?? kind)).slice(0, 300);
  if (reported.has(`${kind}|${message}`)) return;
  reported.add(`${kind}|${message}`);
  reports++;
  const body: CrashBody = {
    kind,
    message,
    stack: (e?.stack ?? '').split('\n').slice(0, 10).join('\n').slice(0, 1500),
    browser: browser(),
    device: device(),
    mode: mode.slice(0, 20),
    build: BUILD.slice(0, 40),
  };
  try {
    // keepalive: it still goes out if the page is closing.
    void fetch('api/crashes', { method: 'POST', body: JSON.stringify(body), keepalive: true, headers: { 'content-type': 'text/plain' } }).catch(() => undefined);
  } catch {
    // Nowhere to send it.
  }
}

/**
 * Errors the game didn't catch, from its own code (not browser extensions'). Promises that fail on the
 * network, or that the browser refuses (sound before a tap), are part of normal life and aren't reported.
 */
export function watchForErrors(mode: () => string): void {
  window.addEventListener('error', (ev) => {
    if (!(ev instanceof ErrorEvent)) return;
    if (ev.filename && !ev.filename.startsWith(location.origin)) return;
    // Code an app slips into the page (a crypto wallet's built-in browser setting window.ethereum) runs as the
    // page's own line 1, where the page has only its doctype.
    if (ev.filename && ev.filename.split('#')[0] === location.href.split('#')[0] && ev.lineno <= 1) return;
    // What a browser says for an error it won't explain (most often a browser extension's): nothing to act on.
    if (ev.message === 'Script error.' && !ev.filename && !ev.error) return;
    reportProblem('error', ev.error ?? ev.message, mode());
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const r: unknown = ev.reason;
    const name = r instanceof Error ? r.name : '';
    const text = r instanceof Error ? r.message : String(r);
    if (name === 'AbortError' || name === 'NotAllowedError' || /fetch|load failed|network/i.test(text)) return;
    reportProblem('error', r, mode());
  });
}

/** Send a player's note. 'ok', or why not ('offline', 'slow down', …). */
export async function sendFeedback(playerId: string, text: string, place: FeedbackBody['place']): Promise<string> {
  const body: FeedbackBody = { playerId, text, browser: browser(), device: device(), place };
  try {
    const r = await fetch('api/feedback', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    const out = (await r.json().catch(() => null)) as { ok: boolean; reason?: string } | null;
    return out?.ok ? 'ok' : (out?.reason ?? `server error ${r.status}`);
  } catch {
    return 'offline';
  }
}

/** What a note goes out with, said to the player: "iPhone · Safari 18". */
export function sentWith(): string {
  return browser();
}
